import React from "react";

import { useSelector } from "react-redux";

import { useAppDispatch } from "src/hooks";
import { getNotifications } from "src/selectors";
import { errorActions } from "src/store/slices";

import { NOTIFICATION_TRANSITION_MS, NOTIFICATION_VISIBLE_MS } from "./constants";

export const useNotificationStack = () => {
  const dispatch = useAppDispatch();
  const notifications = useSelector(getNotifications);
  const [dismissingIds, setDismissingIds] = React.useState<Set<string>>(
    () => new Set()
  );
  const timersRef = React.useRef<Map<string, number>>(new Map());

  const dismiss = React.useCallback((id: string) => {
    const timerId = timersRef.current.get(id);

    if (timerId !== undefined) {
      window.clearTimeout(timerId);
      timersRef.current.delete(id);
    }

    setDismissingIds((prev) => new Set(prev).add(id));
  }, []);

  const remove = React.useCallback(
    (id: string) => {
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);

        return next;
      });
      dispatch(errorActions.notificationDismissed(id));
    },
    [dispatch]
  );

  React.useEffect(() => {
    notifications.forEach(({ id }) => {
      if (timersRef.current.has(id) || dismissingIds.has(id)) {
        return;
      }

      timersRef.current.set(
        id,
        window.setTimeout(() => dismiss(id), NOTIFICATION_VISIBLE_MS)
      );
    });
  }, [dismiss, dismissingIds, notifications]);

  React.useEffect(
    () => () => timersRef.current.forEach((timerId) => clearTimeout(timerId)),
    []
  );

  return {
    dismissingIds,
    dismiss,
    isShow: notifications.length > 0 || dismissingIds.size > 0,
    notifications,
    remove,
    transitionMs: NOTIFICATION_TRANSITION_MS,
  };
};
